import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { Shield, ArrowLeft, Lock, FileLock, Key, Eye, ServerCog, FileCheck2, ArrowDownToLine } from "lucide-react";
import NdaDownloadGate from "@/components/NdaDownloadGate";

/* /security — Procurement-friendly one-pager.
 * Closes the "do you have a security policy?" check-box that almost every
 * agency / care provider procurement form asks. Honest about what we do
 * today; transparent about what we do NOT claim (no HIPAA cert, no SOC 2
 * Type 2 yet) and our roadmap to get there. Also links the downloadable
 * mutual NDA so partners can request one in 30 seconds.
 */
export default function Security() {
    return (
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-16 leading-relaxed text-[#1F2624]">
            <SeoHead
                title="Security & Privacy at Memory Lane — How We Protect Caregiver Data"
                description="Memory Lane Care's security practices — encryption at rest and in transit, HIPAA-aligned controls, data minimization, and how caregiver and patient data is stored."
                canonicalPath="/security"
            />
            <Link to="/" data-testid="security-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-6">
                <ArrowLeft size={14} strokeWidth={1.8} /> Back to Memory Lane
            </Link>
            <header className="border-b border-[#E2E8F0] pb-6 mb-8">
                <div className="flex items-center gap-2.5 text-teal-dark mb-3">
                    <Shield size={22} strokeWidth={1.6} />
                    <span className="text-sm uppercase tracking-wider font-semibold">Memory Lane Care Navigator</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight">Security &amp; Privacy</h1>
                <p className="mt-3 text-sm text-[#4C5551]">For procurement teams · last updated: February 2026</p>
            </header>

            <Section title="The short version">
                <p>
                    Memory Lane is a caregiver-facing app. Caregivers create their own accounts and enter their own notes about their loved one. <strong>We are intentionally not a HIPAA-covered entity</strong> — we treat the data here like a sensitive consumer-grade journal, not a medical record. Below is everything we actually do today, and what we don&apos;t.
                </p>
            </Section>

            <Section title="Data in motion + at rest">
                <Bullet icon={Lock} label="TLS 1.3 in transit">
                    Every request between the browser and our backend is encrypted in transit with TLS 1.3 (managed by our hosting provider).
                </Bullet>
                <Bullet icon={ServerCog} label="Encrypted database at rest">
                    Caregiver data lives in MongoDB with at-rest encryption enabled. Backups are encrypted with the same scheme.
                </Bullet>
                <Bullet icon={Key} label="Secrets managed via environment variables">
                    API keys (LLM provider, email provider, Stripe) live in environment variables on our servers — never committed to source control, never visible in the client.
                </Bullet>
                <Bullet icon={FileLock} label="Passwords hashed with bcrypt">
                    We never store caregiver passwords. We store a one-way bcrypt hash. Even an internal team member with full DB access cannot read a caregiver&apos;s password.
                </Bullet>
            </Section>

            <Section title="Access control">
                <Bullet icon={Eye} label="Caregiver-only visibility">
                    A caregiver&apos;s notes, screener answers, and Lane AI conversations are visible only to <em>that caregiver</em>. Other caregivers cannot see them. Partner organizations cannot see them.
                </Bullet>
                <Bullet icon={Eye} label="Partner orgs see aggregate, de-identified data only">
                    When a caregiver redeems an org&apos;s invite code, the org admin sees a roll-up: cohort size, average burnout band, common behavior themes, engagement rate. They do <strong>not</strong> see individual caregivers&apos; chats, notes, or screener answers. This is a deliberate architectural choice — we built it before we sold it.
                </Bullet>
                <Bullet icon={Eye} label="Memory Lane staff access is minimised">
                    Only one engineer (founder) has production database access today. Access is logged and reviewed.
                </Bullet>
            </Section>

            <Section title="What we deliberately do not do">
                <ul className="list-disc list-inside space-y-1.5 text-base">
                    <li>We do not sell, rent, or share caregiver data with advertisers.</li>
                    <li>We do not use caregiver data to train any AI model. (Conversational memory is per-caregiver, used to make their next chat smarter — never pooled.)</li>
                    <li>We do not store credit card numbers — Stripe Checkout handles all of that.</li>
                    <li>We do not run ad-network pixels, remarketing tags, or cross-site advertising trackers. The analytics tools we <em>do</em> use (GA4, PostHog, and Microsoft Clarity when enabled) are first-party, IP-anonymized where supported, and form-field-masked where supported. The <Link to="/cookies" className="text-teal-dark underline">Cookie Policy</Link> lists every identifier.</li>
                </ul>
            </Section>

            <Section title="What we don&apos;t claim (yet)">
                <p>
                    Honesty matters. Today, Memory Lane is <strong>not</strong> SOC 2 Type 2 certified and is <strong>not</strong> a HIPAA-covered Business Associate. The Privacy Policy explains why that&apos;s deliberate for a consumer-facing caregiver tool. Our roadmap:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-base mt-2">
                    <li><strong>Q2 2026:</strong> Vanta-driven HIPAA control set (training, access reviews, vendor list, incident response policy). BAA available on request for partners who require one.</li>
                    <li><strong>Q4 2026:</strong> SOC 2 Type 1 audit.</li>
                    <li><strong>Q2 2027:</strong> SOC 2 Type 2 (12-month observation period begins after Type 1).</li>
                </ul>
                <p className="mt-3">
                    If your procurement team needs something specific before then — a security questionnaire, our hosting + sub-processor list, a mutual NDA — email <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a> and we&apos;ll turn it around within two business days.
                </p>
            </Section>

            <Section title="Mutual NDA">
                <p>
                    For pilot conversations, we ship the same mutual NDA to every partner — covering both directions, protecting our IP and methodology, and protecting your operational information. Five-year survival period, US law, no copying or reverse-engineering of either side&apos;s confidential information.
                </p>
                <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    aria-hidden="true"
                    className="hidden"
                />
                <NdaDownloadGate
                    testidPrefix="security"
                    trigger={
                        <span
                            data-testid="security-nda-download"
                            className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-teal-dark text-white text-sm font-medium hover:bg-teal cursor-pointer"
                        >
                            <ArrowDownToLine size={14} /> Download our mutual NDA
                        </span>
                    }
                />
            </Section>

            <Section title="Incident response">
                <p>
                    In the unlikely event of a security incident affecting caregiver data, we will:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-base mt-2">
                    <li>Contain the incident, then investigate the scope within 72 hours.</li>
                    <li>Notify affected caregivers via the email they signed up with — within 7 days at the latest.</li>
                    <li>Notify partner orgs (if any of their caregivers are affected) at the same time as the caregivers.</li>
                    <li>Publish a post-mortem on our changelog within 30 days.</li>
                </ul>
            </Section>

            <Section title="A note on your health information">
                <p>
                    Some of what you enter here — behavior notes, medications, care observations — is sensitive, and we treat it that way. We only collect it because you choose to share it with us, and we use it only to make Memory Lane work for you. We don&apos;t sell it. We don&apos;t share it with advertisers. Ever.
                </p>
                <p>
                    If you&apos;re entering information about a family member, we assume you&apos;re their caregiver and have their best interests at heart — that&apos;s who this is built for.
                </p>
            </Section>

            <Section title="Sub-processors">
                <p>The services we use to operate Memory Lane today:</p>
                <div className="overflow-x-auto rounded-2xl border border-cream-dark mt-2">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-cream-light text-left text-xs uppercase tracking-wider text-[#4C5551] border-b border-cream-dark">
                                <th className="py-2 px-3 font-semibold">Provider</th>
                                <th className="py-2 px-3 font-semibold">Purpose</th>
                                <th className="py-2 px-3 font-semibold">Data</th>
                            </tr>
                        </thead>
                        <tbody>
                            <Row p="MongoDB Atlas" purpose="Encrypted database hosting" data="All caregiver-entered data" />
                            <Row p="Hosting cluster" purpose="App + API hosting" data="HTTP requests, app logs" />
                            <Row p="Emergent LLM gateway" purpose="Lane AI conversational guidance (OpenAI, Anthropic, Gemini)" data="Caregiver chat turns; never used to train models" />
                            <Row p="Stripe" purpose="Billing for paid Care Navigator sessions" data="Billing email + card token (we never touch the card)" />
                            <Row p="Resend" purpose="Transactional email (welcome, share link, alerts)" data="Recipient email + email body" />
                            <Row p="Calendly" purpose="Care Navigator session booking" data="Booking name + email + selected slot" />
                            <Row p="Google Analytics 4" purpose="Aggregated traffic, engagement, and conversion analytics (IP anonymized)" data="Pseudonymous client ID, page paths, named events (e.g. Account Created, Roadmap Completed). No PII sent." />
                            <Row p="PostHog" purpose="Product analytics + occasional session replay for bug-fixing" data="Pseudonymous client ID, event metadata. Session-replay input fields are masked by default." />
                            <Row p="Microsoft Clarity" purpose="Heatmaps on our marketing pages so we can see where caregivers get confused and improve the layout. We've turned Clarity off entirely on every page where you enter health information — Lane chat, behavior log, appointment tracker, wellness notes, burnout check. It never sees what you type in those places." data="Pseudonymous visitor ID, click/scroll patterns on informational pages only. Health-data pages never recorded." />
                        </tbody>
                    </table>
                </div>
                <p className="text-sm text-[#4C5551] mt-2">
                    Adding or replacing a sub-processor that handles caregiver data triggers a partner notification at least 30 days in advance.
                </p>
            </Section>

            <Section title="Get in touch">
                <p>
                    Security questions, BAA requests, vendor questionnaires:{" "}
                    <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>.
                    Pilot enquiries:{" "}
                    <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>.
                </p>
            </Section>

            <p className="text-sm text-[#6E7A74] italic mt-12 inline-flex items-center gap-2">
                <FileCheck2 size={14} /> This page describes what we actually do — not aspirational marketing. If anything on it stops being true, we update it within seven days.
            </p>
        </article>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold tracking-tight mb-3">{title}</h2>
            <div className="space-y-3 text-base">{children}</div>
        </section>
    );
}

function Bullet({ icon: Icon, label, children }) {
    return (
        <div className="flex items-start gap-3 py-1">
            <span className="w-9 h-9 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                <Icon size={16} strokeWidth={1.7} />
            </span>
            <div>
                <p className="font-medium text-[#1F2624]">{label}</p>
                <p className="text-[#4C5551] text-[15px] mt-0.5">{children}</p>
            </div>
        </div>
    );
}

function Row({ p, purpose, data }) {
    return (
        <tr className="border-b border-cream-light last:border-b-0 align-top">
            <td className="py-2 px-3 font-medium text-[#1F2624]">{p}</td>
            <td className="py-2 px-3 text-[#4C5551]">{purpose}</td>
            <td className="py-2 px-3 text-[#4C5551]">{data}</td>
        </tr>
    );
}
